/**
 * Baby and Caregiver Types
 */

import { Timestamp } from 'firebase/firestore';

export interface Baby {
  id: string;
  name: string;
  dob: Date;
  gender: 'boy' | 'girl';
  photoURL: string | null;
  ownerId: string;
  caregivers: string[]; // Array of user IDs
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  dietPreference?: 'vegetarian' | 'eggetarian'; // non-veg unlocks after 1yr
  solidsStartDate?: string; // ISO date YYYY-MM-DD, set when parent starts solids
  caregiverRoles?: Record<string, 'full' | 'limited'>; // uid → role for access control
}

export interface BabyInput {
  name: string;
  dob: Date;
  gender: 'boy' | 'girl';
  photoURL?: string | null;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  createdAt: Date;
  lastActiveAt: Date;
}

export interface Invitation {
  id: string;
  email: string;
  babyId: string;
  babyName: string;
  role: 'caregiver' | 'parent';
  invitedBy: string;
  invitedByName: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  token: string; // For shareable link
  createdAt: Date;
  expiresAt: Date;
  acceptedAt: Date | null;
  acceptedByUid: string | null;
  acceptedByName: string | null;
  acceptedByPhotoURL: string | null;
}

export interface InvitationInput {
  email: string;
  babyId: string;
  babyName: string;
  role: 'caregiver' | 'parent';
}

// Helper to convert Firestore data to Baby
export function firestoreToBaby(id: string, data: any): Baby {
  return {
    id,
    name: data.name,
    dob: data.dob instanceof Timestamp ? data.dob.toDate() : new Date(data.dob),
    gender: data.gender,
    photoURL: data.photoURL || null,
    ownerId: data.ownerId,
    caregivers: data.caregivers || [],
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt),
    deletedAt: data.deletedAt ? (data.deletedAt instanceof Timestamp ? data.deletedAt.toDate() : new Date(data.deletedAt)) : null,
  };
}

// Helper to convert Firestore data to UserProfile
export function firestoreToUser(uid: string, data: any): UserProfile {
  return {
    uid,
    email: data.email,
    displayName: data.displayName || null,
    photoURL: data.photoURL || null,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
    lastActiveAt: data.lastActiveAt instanceof Timestamp ? data.lastActiveAt.toDate() : new Date(data.lastActiveAt),
  };
}

// Helper to convert Firestore data to Invitation
export function firestoreToInvitation(id: string, data: any): Invitation {
  return {
    id,
    email: data.email,
    babyId: data.babyId,
    babyName: data.babyName,
    role: data.role,
    invitedBy: data.invitedBy,
    invitedByName: data.invitedByName,
    status: data.status,
    token: data.token,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
    expiresAt: data.expiresAt instanceof Timestamp ? data.expiresAt.toDate() : new Date(data.expiresAt),
    acceptedAt: data.acceptedAt ? (data.acceptedAt instanceof Timestamp ? data.acceptedAt.toDate() : new Date(data.acceptedAt)) : null,
    acceptedByUid: data.acceptedByUid || null,
    acceptedByName: data.acceptedByName || null,
    acceptedByPhotoURL: data.acceptedByPhotoURL || null,
  };
}
